import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  Avatar,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Devices as DevicesIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  BeachAccess as LeaveIcon,
  AddCircle as ApplyLeaveIcon,
  CalendarToday as CalendarIcon,
  AccountCircle as ProfileIcon,
  CalendarMonth as HolidayIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'

const drawerWidth = 210
const collapsedDrawerWidth = 80

const Sidebar = ({ open, onClose, isMobile }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { userType } = useSelector((state) => state.auth)

  // Navigation items for Super Admin
  const superAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/super-admin/dashboard' },
    { text: 'Organizations', icon: <BusinessIcon />, path: '/super-admin/organizations' },
    { text: 'Users', icon: <PeopleIcon />, path: '/super-admin/users' },
    { text: 'Devices', icon: <DevicesIcon />, path: '/super-admin/devices' },
    // { text: 'Audit Logs', icon: <AssessmentIcon />, path: '/super-admin/audit-logs' },
  ]

  // Navigation items for Organization Admin/HR
  const orgAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/org/dashboard' },
    { text: 'Employees', icon: <PeopleIcon />, path: '/org/employees' },
    { text: 'Attendance', icon: <AssignmentIcon />, path: '/org/attendance' },
    { text: 'Holidays', icon: <HolidayIcon />, path: '/org/holidays' },
    { text: 'Leave Management', icon: <LeaveIcon />, path: '/org/leaves' },
    { text: 'Shifts', icon: <ScheduleIcon />, path: '/org/shifts' },
    { text: 'Payroll', icon: <PaymentsIcon />, path: '/org/payroll' },
    { text: 'Devices', icon: <DevicesIcon />, path: '/org/devices' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/org/settings' },
  ]

  // HR specific menu items (subset of org admin)
  const hrMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/org/dashboard' },
    { text: 'Employees', icon: <PeopleIcon />, path: '/org/employees' },
    { text: 'Attendance', icon: <AssignmentIcon />, path: '/org/attendance' },
    { text: 'Holidays', icon: <HolidayIcon />, path: '/org/holidays' },
    { text: 'Leave Management', icon: <LeaveIcon />, path: '/org/leaves' },
    { text: 'Shifts', icon: <ScheduleIcon />, path: '/org/shifts' },
    { text: 'Payroll', icon: <PaymentsIcon />, path: '/org/payroll' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/org/attendance/summary' },
  ]

  // Employee menu items
  const employeeMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/employee/dashboard' },
    { text: 'Attendance', icon: <AssignmentIcon />, path: '/employee/attendance' },
    { text: 'Attendance History', icon: <HistoryIcon />, path: '/employee/attendance/history' },
    { text: 'Holidays', icon: <HolidayIcon />, path: '/employee/holidays' },
    { text: 'Leave Management', icon: <LeaveIcon />, path: '/employee/leave' },
    { text: 'Apply Leave', icon: <ApplyLeaveIcon />, path: '/employee/leave/apply' },
    { text: 'Leave History', icon: <CalendarIcon />, path: '/employee/leave/history' },
    { text: 'My Profile', icon: <ProfileIcon />, path: '/employee/profile' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/employee/settings' },
  ]

  const getMenuItems = () => {
    switch (userType) {
      case 'superadmin':
        return superAdminMenuItems
      case 'admin':
        return orgAdminMenuItems
      case 'hr':
        return hrMenuItems
      case 'employee':
        return employeeMenuItems
      default:
        return []
    }
  }

  const menuItems = getMenuItems()

  const handleNavigation = (path) => {
    navigate(path)
    if (isMobile) {
      onClose()
    }
  }

  // Determine actual drawer width based on state (mobile always full if open, desktop depends on open state)
  const currentDrawerWidth = isMobile ? drawerWidth : (open ? drawerWidth : collapsedDrawerWidth)

  const drawerContent = (
    <Box sx={{ overflowX: 'hidden', overflowY: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Spacer for Fixed Header */}
      <Box sx={{ minHeight: 80 }} /> {/* 76px header + spacing */}

      <List sx={{ pt: 1, flexGrow: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
            <Tooltip title={!open && !isMobile ? item.text : ''} placement="right">
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  mx: 1.5,
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                      : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: theme.palette.common.white,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                        : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.common.white,
                    },
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    // Improve icon color in default state
                    color: location.pathname === item.path ? 'inherit' : theme.palette.text.secondary
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    variant: 'body2',
                  }}
                  sx={{
                    opacity: open ? 1 : 0,
                    display: open ? 'block' : 'none',
                    // Ensure text doesn't wrap awkwardly during transition
                    whiteSpace: 'nowrap',
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: currentDrawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: currentDrawerWidth,
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          boxSizing: 'border-box',
          background: theme.palette.background.paper,
          borderRight: `1px dashed ${theme.palette.divider}`,
          boxShadow: open ? 'none' : 'none', // Border is enough for separation
          height: '100%', // Full height
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar